import { Injectable } from '@angular/core';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Location } from '../models/location.model';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { GetLocationPayload, SubdomainLookupResponse } from './location.types';
import { RawSchedule } from '../lib/location-schedule';
import { BrandStore } from './brand.store';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private dataUrl: string = 'locations/details/';
  protected location: Location = {} as Location;
  private scheduleRequests = new Map<string, Observable<RawSchedule[]>>();
  locationChanged = new Subject<Location>();

  constructor(
    private readonly http: HttpClient,
    private router: Router,
    private brandStore: BrandStore,
  ) {}

  getLocation(): Location {
    return this.location;
  }

  setLocation(location: Location): void {
    this.location = location;
    this.brandStore.setFromPartnerType(location.partnerType);
    this.locationChanged.next(this.location);
  }

  getCurrency(): string {
    return this.location.currency;
  }

  fetchLocation(locationUuid: string): Observable<Location | undefined> {
    return this.http.get<GetLocationPayload>(this.dataUrl + locationUuid).pipe(
      map((payload) => {
        const location: Location = {
          ...payload.location,
          paymentGateways: payload.paymentGateways,
          customFields: payload.customFields,
        };

        this.setLocation(location);

        return location;
      }),
      catchError(() => {
        this.router.navigate(['/page-not-found']);
        return of(undefined);
      }),
    );
  }

  fetchSchedule(uuid: string): Observable<RawSchedule[]> {
    const cachedRequest = this.scheduleRequests.get(uuid);

    if (cachedRequest) {
      return cachedRequest;
    }

    const request = this.http.get<{ locationSchedule: RawSchedule[] }>(`locations/schedule/${uuid}`).pipe(
      map((res) => res.locationSchedule),
      catchError((error) => {
        this.scheduleRequests.delete(uuid);
        return throwError(() => error);
      }),
      shareReplay(1),
    );

    this.scheduleRequests.set(uuid, request);

    return request;
  }

  fetchSubdomainLookup(): Observable<SubdomainLookupResponse> {
    return this.http.get<SubdomainLookupResponse>('locations/uuid/');
  }
}
